import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ShoppingCart,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Package,
  AlertTriangle
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from '../../hooks/use-toast';

const statusColors = {
  pending_payment: 'bg-yellow-900 text-yellow-300',
  pending: 'bg-yellow-900 text-yellow-300',
  paid: 'bg-green-900 text-green-300',
  accepted_by_seller: 'bg-blue-900 text-blue-300',
  searching_driver: 'bg-purple-900 text-purple-300',
  driver_assigned: 'bg-cyan-900 text-cyan-300',
  in_transit: 'bg-indigo-900 text-indigo-300',
  confirmed: 'bg-blue-900 text-blue-300',
  shipped: 'bg-purple-900 text-purple-300',
  delivered: 'bg-green-900 text-green-300',
  delivered_local: 'bg-green-900 text-green-300',
  delivered_national: 'bg-green-900 text-green-300',
  cancelled: 'bg-red-900 text-red-300',
};

const statusLabels = {
  pending_payment: 'Pend. Pago',
  pending: 'Pendiente',
  paid: 'Pagado',
  accepted_by_seller: 'Aceptado',
  searching_driver: 'Buscando Rep.',
  driver_assigned: 'Rep. Asignado',
  in_transit: 'En Transito',
  confirmed: 'Confirmado',
  shipped: 'Enviado',
  delivered: 'Entregado',
  delivered_local: 'Ent. Local',
  delivered_national: 'Ent. Nacional',
  cancelled: 'Cancelado',
};

/* ===== Tabs ===== */
const TABS = [
  { key: 'orders', label: 'Pedidos' },
  { key: 'abandoned', label: 'Carritos Abandonados' },
];

/* ==================== ABANDONED CARTS SUB-COMPONENT ==================== */
const AbandonedCarts = () => {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [data, countData] = await Promise.all([
        adminAPI.getAbandonedCarts({ skip: page * limit, limit }),
        adminAPI.getAbandonedCartsCount()
      ]);
      setCarts(data);
      setCount(countData.count);
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: 'Error al cargar carritos abandonados', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(count / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Carritos Abandonados</h2>
          <p className="text-gray-400 mt-1">{count} carritos inactivos (&gt; 2 h)</p>
        </div>
        <Button onClick={load} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      <Card className="bg-gray-800 border-gray-700 overflow-hidden" data-testid="abandoned-carts-table">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : carts.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No hay carritos abandonados</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Items</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Última act.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {carts.map((cart, idx) => (
                  <tr key={cart.cart_id || idx} className="hover:bg-gray-700/50" data-testid={`abandoned-cart-row-${idx}`}>
                    <td className="px-4 py-4 text-white">{cart.user_name || 'N/A'}</td>
                    <td className="px-4 py-4 text-gray-400 text-sm">{cart.user_email || ''}</td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        {cart.items?.map((item, i) => (
                          <p key={i} className="text-white text-sm">
                            {item.title} <span className="text-gray-400">x{item.quantity}</span>
                          </p>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-white font-medium">{formatPrice(cart.total || 0)}</td>
                    <td className="px-4 py-4 text-gray-400 text-sm">
                      {cart.updated_at ? new Date(cart.updated_at).toLocaleString('es-MX') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, count)} de {count}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-gray-600 text-gray-300">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="border-gray-600 text-gray-300">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ==================== MAIN COMPONENT ==================== */
const AdminOrders = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const limit = 20;

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const [ordersData, countData] = await Promise.all([
        adminAPI.getOrders({ skip: page * limit, limit, status: statusFilter }),
        adminAPI.getOrdersCount({ status: statusFilter })
      ]);
      setOrders(ordersData);
      setTotalCount(countData.count);
    } catch (err) {
      console.error('Error loading orders:', err);
      toast({ title: 'Error', description: 'Error al cargar órdenes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    if (activeTab === 'orders') loadOrders();
  }, [loadOrders, activeTab]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await adminAPI.updateOrderStatus(orderId, newStatus);
      loadOrders();
      toast({ title: 'Éxito', description: 'Estado de la orden actualizado' });
    } catch (err) {
      toast({ title: 'Error', description: 'Error al actualizar orden', variant: 'destructive' });
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="admin-orders-title">Órdenes</h1>
            <p className="text-gray-400 mt-1">{totalCount} órdenes en total</p>
          </div>
          {activeTab === 'orders' && (
            <Button onClick={loadOrders} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
              <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-700 pb-1" data-testid="admin-orders-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
                activeTab === tab.key
                  ? 'bg-gray-800 text-orange-400 border-b-2 border-orange-400'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.key === 'abandoned' && <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5" />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'abandoned' ? (
          <AbandonedCarts />
        ) : (
          <>
            {/* Filters */}
            <Card className="bg-gray-800 border-gray-700 p-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={statusFilter === null ? 'default' : 'outline'}
                  onClick={() => { setStatusFilter(null); setPage(0); }}
                  className={statusFilter === null ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}
                  size="sm"
                >
                  Todos
                </Button>
                {Object.entries(statusLabels).map(([key, label]) => (
                  <Button
                    key={key}
                    variant={statusFilter === key ? 'default' : 'outline'}
                    onClick={() => { setStatusFilter(key); setPage(0); }}
                    className={statusFilter === key ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}
                    size="sm"
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Orders table */}
            <Card className="bg-gray-800 border-gray-700 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No se encontraron órdenes</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID Orden</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Comprador</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Items</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {orders.map((order) => (
                        <tr key={order._id} className="hover:bg-gray-700/50">
                          <td className="px-4 py-4">
                            <span className="text-white font-mono text-sm">
                              #{order._id?.slice(-8).toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-gray-400 text-sm">
                            {new Date(order.created_at).toLocaleDateString('es-MX')}
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-white">{order.buyer_name || 'N/A'}</p>
                            <p className="text-gray-400 text-sm">{order.buyer_email || ''}</p>
                          </td>
                          <td className="px-4 py-4 text-white">
                            {order.items?.length || 0} productos
                          </td>
                          <td className="px-4 py-4 text-white font-medium">
                            {formatPrice(order.total_amount || order.total)}
                          </td>
                          <td className="px-4 py-4">
                            <select
                              value={order.status}
                              onChange={(e) => handleStatusChange(order._id, e.target.value)}
                              className="px-2 py-1 rounded text-xs font-medium bg-gray-700 border-gray-600 text-white cursor-pointer"
                            >
                              {Object.entries(statusLabels).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/admin/orders/${order.order_id || order._id}`)}
                                className="text-gray-400 hover:text-white"
                                title="Ver detalles"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
                  <p className="text-sm text-gray-400">
                    Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} de {totalCount}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="border-gray-600 text-gray-300"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= totalPages - 1}
                      className="border-gray-600 text-gray-300"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Order detail modal */}
            {selectedOrder && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="bg-gray-800 border-gray-700 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-white">
                        Orden #{selectedOrder._id?.slice(-8).toUpperCase()}
                      </h2>
                      <Button
                        variant="ghost"
                        onClick={() => setSelectedOrder(null)}
                        className="text-gray-400 hover:text-white"
                      >
                        ✕
                      </Button>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm">Fecha</p>
                          <p className="text-white">{new Date(selectedOrder.created_at).toLocaleString('es-MX')}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Estado</p>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[selectedOrder.status]}`}>
                            {statusLabels[selectedOrder.status]}
                          </span>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Comprador</p>
                          <p className="text-white">{selectedOrder.buyer_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Total</p>
                          <p className="text-white font-bold">{formatPrice(selectedOrder.total_amount || selectedOrder.total)}</p>
                        </div>
                      </div>

                      <div className="border-t border-gray-700 pt-4">
                        <p className="text-gray-400 text-sm mb-3">Productos</p>
                        <div className="space-y-3">
                          {selectedOrder.items?.map((item, index) => (
                            <div key={index} className="flex items-center gap-3 bg-gray-700/50 p-3 rounded">
                              <Package className="w-8 h-8 text-gray-500" />
                              <div className="flex-1">
                                <p className="text-white">{item.title}</p>
                                <p className="text-gray-400 text-sm">Cantidad: {item.quantity}</p>
                              </div>
                              <p className="text-white">{formatPrice(item.price * item.quantity)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
